/**
 * GlobalBoqDashboard Component
 * Centralized dashboard for creating and managing BOQs/Estimates across all projects.
 */

import {
	Calculator,
	ExternalLink,
	Eye,
	FileSpreadsheet,
	Plus,
	Printer,
	Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	Eyebrow,
	FloatPanel,
	ItalicAccent,
	Pill,
	type PillTone,
	StudioButton,
} from "@/features/studio/theme";
import { openBoqPrintView } from "../components/boq/open-boq-print-view";
import {
	type BoqStatus,
	formatCurrency,
	type GlobalBOQItem,
	getAllBOQs,
} from "../services/boq/boq-api.service";
import {
	getContractorAssignments,
	getDesignerProjects,
	type Project,
} from "../services/projects-api.service";

const STATUS_TONE: Record<BoqStatus, PillTone> = {
	draft: "neutral",
	sent: "info",
	change_requested: "awaiting",
	approved: "done",
	locked: "active",
};

const STATUS_FRIENDLY_LABEL: Record<BoqStatus, string> = {
	draft: "Draft Estimate",
	sent: "Sent to Client",
	change_requested: "Client Feedback",
	approved: "Approved",
	locked: "Finalized & Locked",
};

/**
 * Projects the caller can start a BOQ on. `/projects/designer` is designer-only,
 * so a contractor reads the projects they are hired on from their assignments.
 * Only a `hired` assignment may create a BOQ (`verifyProjectAccess`); one that
 * has asked for completion or finished is listed by the assignments but refused.
 */
async function loadBoqProjects(
	userType: string | null | undefined,
): Promise<Project[]> {
	if (userType !== "contractor") return getDesignerProjects();
	const assignments = await getContractorAssignments();
	const byId = new Map<string, Project>();
	for (const assignment of assignments) {
		if (assignment.status === "hired" && assignment.project) {
			byId.set(assignment.project.id, assignment.project);
		}
	}
	return [...byId.values()];
}

export function GlobalBoqDashboard() {
	const navigate = useNavigate();
	const userType = useAuthStore((state) => state.user?.userType);

	const [boqs, setBoqs] = useState<GlobalBOQItem[]>([]);
	const [projects, setProjects] = useState<Project[]>([]);
	// Set when the project list could not be loaded, as opposed to being empty.
	const [projectsLoadFailed, setProjectsLoadFailed] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [searchQuery, setSearchQuery] = useState("");
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [createMode, setCreateMode] = useState<"blank" | "starter">("blank");

	useEffect(() => {
		let isMounted = true;
		const loadData = async () => {
			setIsLoading(true);
			try {
				// Settled separately: a project list that fails to load must not
				// blank the BOQs that did.
				const [boqResult, projResult] = await Promise.allSettled([
					getAllBOQs(),
					loadBoqProjects(userType),
				]);
				if (!isMounted) return;
				if (boqResult.status === "fulfilled") {
					setBoqs(boqResult.value.data || []);
				} else {
					console.error("Failed to load global BOQs:", boqResult.reason);
				}
				if (projResult.status === "fulfilled") {
					setProjectsLoadFailed(false);
					setProjects(projResult.value || []);
					if (projResult.value?.[0]) {
						setSelectedProjectId(projResult.value[0].id);
					}
				} else {
					console.error("Failed to load projects:", projResult.reason);
					setProjectsLoadFailed(true);
					setProjects([]);
					setSelectedProjectId("");
				}
			} finally {
				if (isMounted) setIsLoading(false);
			}
		};
		loadData();
		return () => {
			isMounted = false;
		};
	}, [userType]);

	// Filtered list
	const filteredBoqs = useMemo(() => {
		return boqs.filter((b) => {
			if (statusFilter !== "all" && b.status !== statusFilter) {
				return false;
			}
			if (searchQuery.trim()) {
				const q = searchQuery.toLowerCase();
				const matchTitle = (b.title || "").toLowerCase().includes(q);
				const matchProject = (b.projectName || "").toLowerCase().includes(q);
				const matchNotes = (b.notes || "").toLowerCase().includes(q);
				if (!matchTitle && !matchProject && !matchNotes) return false;
			}
			return true;
		});
	}, [boqs, statusFilter, searchQuery]);

	// Metric summaries
	const metrics = useMemo(() => {
		const totalEstimates = boqs.length;
		const approvedValue = boqs
			.filter((b) => b.status === "approved" || b.status === "locked")
			.reduce((sum, b) => sum + (b.grandTotal ?? 0), 0);
		const pendingCount = boqs.filter(
			(b) => b.status === "sent" || b.status === "change_requested",
		).length;
		const draftCount = boqs.filter((b) => b.status === "draft").length;

		return {
			totalEstimates,
			approvedValue,
			pendingCount,
			draftCount,
		};
	}, [boqs]);

	// Only a designer or contractor can author a BOQ.
	const canCreate = userType === "designer" || userType === "contractor";

	// An empty selection is "No project": a standalone estimate, private to its
	// creator. It can start from a starter template like a project BOQ can.
	const isStandalone = !selectedProjectId;

	const handleProceedToCreate = () => {
		const query = createMode === "starter" ? "?mode=starter" : "";
		navigate(
			isStandalone
				? `/boqs/new${query}`
				: `/projects/${selectedProjectId}/boqs/new${query}`,
		);
	};

	return (
		<div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
			{/* ── Page Header ─────────────────────────────────────────────── */}
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<Eyebrow style={{ fontSize: 9 }}>
						ESTIMATES & BILL OF QUANTITIES
					</Eyebrow>
					<h1
						className="text-dashboard-text"
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 34,
							lineHeight: 1.05,
							letterSpacing: "-0.02em",
							margin: "8px 0 0",
						}}
					>
						Estimates &{" "}
						<ItalicAccent
							style={{
								fontSize: 34,
								color: "var(--color-dashboard-accent-display)",
							}}
						>
							BOQs
						</ItalicAccent>
					</h1>
					<p
						className="text-sm mt-1"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						Create, track, and manage all client estimates and specifications
						across projects.
					</p>
				</div>

				<div className="flex items-center gap-2">
					<StudioButton
						variant="secondary"
						size="md"
						icon={FileSpreadsheet}
						onClick={() => navigate("/settings/boq")}
					>
						Manage Templates
					</StudioButton>
					{canCreate && (
						<StudioButton
							variant="primary"
							size="md"
							icon={Plus}
							onClick={() => setShowCreateDialog(true)}
						>
							New BOQ / Estimate
						</StudioButton>
					)}
				</div>
			</div>

			{/* ── Metric Summary Cards ────────────────────────────────────── */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
				<FloatPanel elevation={1} padding={18}>
					<div className="text-xs uppercase font-mono tracking-wider text-dashboard-muted mb-1">
						Total Estimates
					</div>
					<div
						className="text-2xl font-semibold text-dashboard-text"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{metrics.totalEstimates}
					</div>
				</FloatPanel>

				<FloatPanel elevation={1} padding={18}>
					<div className="text-xs uppercase font-mono tracking-wider text-dashboard-muted mb-1">
						Approved Value
					</div>
					<div
						className="text-2xl font-semibold text-dashboard-accent-dark"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{formatCurrency(metrics.approvedValue, "INR")}
					</div>
				</FloatPanel>

				<FloatPanel elevation={1} padding={18}>
					<div className="text-xs uppercase font-mono tracking-wider text-dashboard-muted mb-1">
						Awaiting Client
					</div>
					<div
						className="text-2xl font-semibold text-dashboard-pill-awaiting"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{metrics.pendingCount}
					</div>
				</FloatPanel>

				<FloatPanel elevation={1} padding={18}>
					<div className="text-xs uppercase font-mono tracking-wider text-dashboard-muted mb-1">
						Drafts in Progress
					</div>
					<div
						className="text-2xl font-semibold text-dashboard-muted-dark"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{metrics.draftCount}
					</div>
				</FloatPanel>
			</div>

			{/* ── Toolbar: Search & Filters ───────────────────────────────── */}
			<div className="flex flex-wrap items-center justify-between gap-4">
				<div className="flex items-center gap-2 flex-1 max-w-md">
					<div className="relative w-full">
						<Search
							size={15}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-dashboard-muted"
						/>
						<Input
							type="text"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							placeholder="Search by title, project name, or notes..."
							className="pl-9 bg-dashboard-paper border-dashboard-border"
						/>
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-1.5">
					{[
						{ label: "All", value: "all" },
						{ label: "Draft", value: "draft" },
						{ label: "Sent", value: "sent" },
						{ label: "Feedback", value: "change_requested" },
						{ label: "Approved", value: "approved" },
						{ label: "Locked", value: "locked" },
					].map((tab) => (
						<button
							key={tab.value}
							type="button"
							onClick={() => setStatusFilter(tab.value)}
							style={{
								padding: "5px 12px",
								fontSize: 12,
								fontFamily: "var(--font-mono)",
								borderRadius: 6,
								border:
									statusFilter === tab.value
										? "1px solid var(--color-dashboard-accent)"
										: "1px solid var(--color-dashboard-border)",
								background:
									statusFilter === tab.value
										? "var(--color-dashboard-warm-100)"
										: "var(--color-dashboard-paper)",
								color:
									statusFilter === tab.value
										? "var(--color-dashboard-accent-dark)"
										: "var(--color-dashboard-muted)",
								cursor: "pointer",
								transition: "all 150ms",
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
			</div>

			{/* ── Table of BOQs ───────────────────────────────────────────── */}
			<FloatPanel elevation={1} padding={0}>
				{isLoading ? (
					<div className="p-6 space-y-4">
						{[1, 2, 3, 4].map((i) => (
							<Skeleton
								key={i}
								className="h-14 w-full rounded-lg bg-dashboard-surface"
							/>
						))}
					</div>
				) : filteredBoqs.length === 0 ? (
					<div className="p-12 text-center space-y-3">
						<Calculator size={36} className="mx-auto text-dashboard-muted" />
						<div className="text-base font-medium text-dashboard-text">
							{searchQuery || statusFilter !== "all"
								? "No estimates match your filters"
								: "No estimates or BOQs created yet"}
						</div>
						<p className="text-xs text-dashboard-muted max-w-md mx-auto">
							{searchQuery || statusFilter !== "all"
								? "Try resetting search terms or changing status filter."
								: canCreate
									? "Start by creating an estimate from scratch or using one of our curated interior templates."
									: "There are no estimates to show yet."}
						</p>
						{canCreate && !searchQuery && statusFilter === "all" && (
							<Button
								variant="default"
								size="sm"
								className="mt-2"
								onClick={() => setShowCreateDialog(true)}
							>
								<Plus className="mr-1.5 h-4 w-4" /> Create First Estimate
							</Button>
						)}
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-left border-collapse">
							<thead>
								<tr
									style={{
										background: "var(--color-dashboard-surface)",
										borderBottom:
											"1px solid var(--color-dashboard-border-soft)",
									}}
								>
									<th className="py-3 px-4 text-xs font-mono uppercase text-dashboard-placeholder">
										Project
									</th>
									<th className="py-3 px-4 text-xs font-mono uppercase text-dashboard-placeholder">
										Estimate / Title
									</th>
									<th className="py-3 px-4 text-xs font-mono uppercase text-dashboard-placeholder">
										Version
									</th>
									<th className="py-3 px-4 text-xs font-mono uppercase text-dashboard-placeholder">
										Status
									</th>
									<th className="py-3 px-4 text-xs font-mono uppercase text-dashboard-placeholder text-right">
										Total (₹)
									</th>
									<th className="py-3 px-4 text-xs font-mono uppercase text-dashboard-placeholder">
										Updated
									</th>
									<th className="py-3 px-4 text-xs font-mono uppercase text-dashboard-placeholder text-right">
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{filteredBoqs.map((boq, idx) => (
									<tr
										key={boq.id}
										style={{
											borderBottom:
												idx < filteredBoqs.length - 1
													? "1px solid var(--color-dashboard-surface)"
													: "none",
										}}
										className="hover:bg-dashboard-surface/40 transition-colors group"
									>
										{/* Project Name */}
										<td className="py-3.5 px-4">
											{boq.projectId === null ? (
												<span className="font-medium text-sm text-dashboard-muted">
													No project
												</span>
											) : (
												<button
													type="button"
													onClick={() =>
														navigate(`/projects/${boq.projectId}?tab=boq`)
													}
													className="font-medium text-sm text-dashboard-text hover:text-dashboard-accent transition-colors flex items-center gap-1.5"
													style={{
														background: "none",
														border: "none",
														cursor: "pointer",
														padding: 0,
													}}
												>
													{boq.projectName || "Project"}
													<ExternalLink
														size={12}
														className="opacity-0 group-hover:opacity-60 transition-opacity"
													/>
												</button>
											)}
											{boq.projectCode && (
												<div className="text-[11px] font-mono text-dashboard-placeholder">
													{boq.projectCode}
												</div>
											)}
										</td>

										{/* Title */}
										<td className="py-3.5 px-4">
											<div className="font-medium text-sm text-dashboard-text">
												{boq.title || "Bill of Quantities"}
											</div>
											{boq.notes && (
												<div className="text-xs text-dashboard-muted truncate max-w-xs">
													{boq.notes}
												</div>
											)}
										</td>

										{/* Version */}
										<td className="py-3.5 px-4 font-mono text-xs text-dashboard-muted">
											v{boq.versionNumber}
										</td>

										{/* Status */}
										<td className="py-3.5 px-4">
											<Pill tone={STATUS_TONE[boq.status] ?? "neutral"}>
												{STATUS_FRIENDLY_LABEL[boq.status] ?? boq.status}
											</Pill>
										</td>

										{/* Grand Total */}
										<td className="py-3.5 px-4 text-right font-mono font-semibold text-sm text-dashboard-text">
											{formatCurrency(boq.grandTotal ?? 0, boq.currencyCode)}
										</td>

										{/* Updated */}
										<td className="py-3.5 px-4 text-xs font-mono text-dashboard-muted">
											{new Date(boq.updatedAt).toLocaleDateString()}
										</td>

										{/* Actions */}
										<td className="py-3.5 px-4 text-right">
											<div className="flex items-center justify-end gap-1.5">
												<button
													type="button"
													onClick={() =>
														navigate(
															boq.projectId === null
																? `/boqs/${boq.id}`
																: `/projects/${boq.projectId}/boqs/${boq.id}`,
														)
													}
													title="View details"
													aria-label="View estimate"
													className="p-1.5 rounded hover:bg-dashboard-surface text-dashboard-muted hover:text-dashboard-text transition-colors"
												>
													<Eye size={15} />
												</button>
												<button
													type="button"
													onClick={() => openBoqPrintView(boq.id)}
													title="Print / PDF"
													aria-label="Print estimate"
													className="p-1.5 rounded hover:bg-dashboard-surface text-dashboard-muted hover:text-dashboard-text transition-colors"
												>
													<Printer size={15} />
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</FloatPanel>

			{/* ── Create BOQ Project Picker Dialog ────────────────────────── */}
			<Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
				<DialogContent className="sm:max-w-md bg-dashboard-paper border-dashboard-border">
					<DialogHeader>
						<DialogTitle
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 20,
								color: "var(--color-dashboard-text)",
							}}
						>
							Select Project for BOQ / Estimate
						</DialogTitle>
						<DialogDescription className="sr-only">
							Choose project for creating a new BOQ or estimate
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<p className="text-sm text-dashboard-muted">
							Choose which active project you are authoring this BOQ / estimate
							for, or create it without a project:
						</p>

						<div className="space-y-3">
							<div className="space-y-2">
								<label
									htmlFor="create-boq-project-select"
									className="text-xs font-mono uppercase text-dashboard-muted tracking-wider block"
								>
									Project
								</label>
								<select
									id="create-boq-project-select"
									value={selectedProjectId}
									onChange={(e) => setSelectedProjectId(e.target.value)}
									className="w-full p-2.5 rounded-lg border border-dashboard-border bg-dashboard-surface text-dashboard-text text-sm outline-none"
								>
									<option value="">No project (standalone estimate)</option>
									{projects.map((proj) => (
										<option key={proj.id} value={proj.id}>
											{proj.title}
										</option>
									))}
								</select>
								{projectsLoadFailed && (
									<p className="text-[11px] text-dashboard-muted">
										Your projects could not be loaded, so only a standalone
										estimate can be created right now.
									</p>
								)}
								{isStandalone && (
									<p className="text-[11px] text-dashboard-muted">
										A standalone estimate is private to you and cannot be sent
										to a client.
									</p>
								)}
							</div>

							<div className="space-y-2">
								<div className="text-xs font-mono uppercase text-dashboard-muted tracking-wider block">
									Creation Mode
								</div>
								<div className="grid grid-cols-2 gap-2">
									<button
										type="button"
										onClick={() => setCreateMode("blank")}
										className={`p-2.5 rounded-lg border text-left text-xs transition-colors ${
											createMode === "blank"
												? "border-dashboard-accent bg-dashboard-surface text-dashboard-text font-medium"
												: "border-dashboard-border bg-dashboard-paper text-dashboard-muted hover:border-dashboard-border-hover"
										}`}
									>
										<div className="font-semibold text-dashboard-text mb-0.5">
											Start Blank
										</div>
										<div className="text-[11px] text-dashboard-muted">
											Create custom line items from scratch
										</div>
									</button>
									<button
										type="button"
										onClick={() => setCreateMode("starter")}
										className={`p-2.5 rounded-lg border text-left text-xs transition-colors ${
											createMode === "starter"
												? "border-dashboard-accent bg-dashboard-surface text-dashboard-text font-medium"
												: "border-dashboard-border bg-dashboard-paper text-dashboard-muted hover:border-dashboard-border-hover"
										}`}
									>
										<div className="font-semibold text-dashboard-text mb-0.5">
											Use Template
										</div>
										<div className="text-[11px] text-dashboard-muted">
											Pre-fill with a Finishd starter template
										</div>
									</button>
								</div>
							</div>
						</div>

						<div className="flex justify-end gap-2 pt-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowCreateDialog(false)}
							>
								Cancel
							</Button>
							<Button
								variant="default"
								size="sm"
								onClick={handleProceedToCreate}
							>
								Continue to Create BOQ
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
